import { Paper, Group, Title } from '@mantine/core';
import { IconFileDownload } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { RowSelectionState } from '@tanstack/table-core';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import {
  FORM_STATUS,
  insuranceApi,
  useColFilters,
  useSharedQueries,
} from '@data-access';
import {
  ActionsMenu,
  ActionsMenuItem,
  FullMainHeight,
  InsurancesTable,
} from '@ui';

export const Route = createFileRoute('/_app/insurance')({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const [filters, setFilters] = useColFilters('insurances');
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [computationRequestId, setComputationRequestId] = React.useState<
    number | null
  >(null);

  const selectedRowsIds = Object.keys(rowSelection).map((key) => Number(key));

  const exportSynthesis = useMutation({
    mutationFn: insuranceApi.exportSynthesis,
    onSuccess: (data) => {
      setComputationRequestId(data.instance_id);
    },
  });
  const downloadSynthesis = useMutation({
    mutationFn: insuranceApi.downloadExport,
  });
  const sharedQueries = useSharedQueries();
  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });

  const handleDownloadRecaps = () => {
    if (rowSelection.all) {
      exportSynthesis.mutate({
        ids: [],
        all: true,
        selectedFilters: filters,
      });
    } else {
      exportSynthesis.mutate({
        ids: selectedRowsIds,
      });
    }
  };

  const actions: ActionsMenuItem[] = [
    {
      type: 'action',
      icon: IconFileDownload,
      label: t('common.export'),
      onClick: () => {
        handleDownloadRecaps();
      },
    },
  ];

  React.useEffect(() => {
    if (computationRequest.data?.status === FORM_STATUS.FINISHED) {
      downloadSynthesis.mutate({
        id: computationRequest.data.id,
      });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadSynthesis]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Header>
        <Paper>
          <Group justify="space-between">
            <Title order={2}>{t('insurances.list_title')}</Title>
            <Group>
              <ActionsMenu items={actions}>
                <ActionsMenu.Button
                  loading={
                    computationRequest.data?.status === FORM_STATUS.PENDING
                  }
                />
              </ActionsMenu>
            </Group>
          </Group>
        </Paper>
      </FullMainHeight.Header>
      <FullMainHeight.Content>
        <InsurancesTable
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
        />
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}
